
import {
  Directive, EventEmitter, Input, Output, inject,
} from '@angular/core';

import { DynamicFormFieldModel } from 'src/app/@shared/form/dynamic-form-field/dynamic-form-field.model';
import {
  DynamicFormValues, FormConfig,
  FormFieldKey,
  FormFieldList, FormFieldValue, FormFieldsetCategory, FormFieldsetConfig,
} from 'src/app/@shared/form/form.types';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { FormFieldModel } from 'src/app/@shared/form/form-field/form-field.model';
import { FormKey } from 'src/app/@shared/form/form-config';
import { FormMainType } from '@lib/types';
import { LoadingController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import {
  createFieldLists,
  createFieldsByConfig,
  createForm, getConfigs,
  getFormKey,
} from 'src/app/@shared/form/form-helper';

export type DatasetFormStatus = 'new' | 'edit' | 'review-request' | 'to-be-reviewed' | 'accepted' | 'denied';
export type DatasetFormCompleteStatus = 'canceled' | 'submitted' | 'denied' | 'error';

export type DatasetFormData = { [key in FormFieldKey]?: any };

// TODO: only id or are there other fields?
const FIELDS_NOT_ALLOWED_TO_BE_NULL: FormFieldKey[] = ['id'];

@Directive()
export abstract class BaseFormComponent<T extends { type: FormMainType }> {
  @Output() public processCompleted: EventEmitter<DatasetFormCompleteStatus> = new EventEmitter<DatasetFormCompleteStatus>();

  public formId: FormKey;
  public editForm: FormGroup<DatasetFormData> = new FormGroup({});
  public formFieldLists: FormFieldList[] = [];
  public formCategories: FormFieldsetCategory[] = [];
  public selectedCategory: FormFieldsetCategory = 'all';

  public isLoading: boolean;
  public hiddenFieldIds: FormFieldKey[] = [];

  // for consistency each property expects the same type here
  // e.g. emails is [{ emails: 'john@doe.de' }, { emails: 'john2@doe.de' }] instead of ['john@doe.de', 'john2@doe.de']
  // this makes it easier for us to handle array of formArrays and array of formControls with quite the same code base
  // TODO: check for missing fields
  public dynamicFields: { [key in FormFieldKey]?: DynamicFormValues[] } = {
    contactPersons: [],
    emails: [],
    icd10Codes: [],
    icd11Codes: [],
    links: [],
    orphanetSynonyms: [],
    phoneContacts: [],
    socialMediaLinks: [],
    synonyms: [],
    websites: [],
  };

  public formIsBlocked: boolean;

  protected loadingController = inject(LoadingController);
  protected translate = inject(TranslateService);
  protected allFieldConfigs: (FormFieldModel | DynamicFormFieldModel)[] = [];
  protected datasetData: T;

  @Input()
  public get data(): T {
    return this.datasetData;
  }

  // we need at least data.type to initialize our form
  public set data(data: T) {
    // item is initially null (skeleton)
    if (!data?.type) {
      return;
    }

    this.onDataReceived(data);
  }

  // workaround for having typed variables in ng-template
  // (see https://stackoverflow.com/questions/55458421/ng-template-typed-variable)
  public asFormControl(fieldId: FormFieldKey): FormControl {
    return this.editForm.get(fieldId) as FormControl;
  }

  public asFormArray(fieldId: FormFieldKey): FormArray {
    return this.editForm.get(fieldId) as FormArray;
  }

  public debugShowFormData(): void {
    // // properties in order
    //
    // const sortedFields: FormFieldModel[] = this.formFieldLists
    // .map((fieldList) => { return fieldList.items; }).flat();
    //
    // for (const field of sortedFields) {
    // const { value } = this.editForm.get(field.id);
    //
    // if (Array.isArray(value)) {
    //     if (value.length) {
    //       console.info(field.id, value);
    //     }
    //
    //     // eslint-disable-next-line no-undefined
    // } else if (value !== undefined && value !== null) {
    //     console.info(field.id, value);
    // }
    // }

    console.info('sanitized form data', this.getSanitizedDatasetData<DatasetFormData>(this.editForm.value));
  }

  public formIsValid(): boolean {
    return this.editForm.valid && this.editForm.dirty;
  }

  public async submit(isDenied?: boolean): Promise<void> {
    if ((!this.formIsValid() && !isDenied) || this.isLoading) {
      return Promise.resolve();
    }

    this.isLoading = true;

    const loadingMessage = await this.loadingController.create({
      message: this.translate.instant('VIEW.COMMON.LOADING_MESSAGE.SAVE_CHANGES'),
    });

    await loadingMessage.present();

    let requestSucceeded: boolean;

    try {
      const data = this.getSanitizedDatasetData<DatasetFormData>(this.editForm.value);

      requestSucceeded = await this.handleRequest(data, isDenied);
    } catch (error) {
      console.error(error);
      requestSucceeded = false;
    }

    this.isLoading = false;
    loadingMessage.dismiss().catch();

    this.onRequestFinished(requestSucceeded, isDenied);

    return Promise.resolve();
  }

  // eslint-disable-next-line class-methods-use-this
  protected onDataReceived(data: T): void {
    console.warn('[BaseFormComponent] onSetData needs to be specified for the component to initialize the data correctly!', data);
  }

  protected prepareForm(): void {
    this.formId = this.getFormId();

    const { formConfig, fieldsetConfig } = getConfigs(this.formId);

    console.info('### formconfig', formConfig);

    if (!formConfig || !Object.keys(formConfig).length) {
      console.error('formConfig missing', this.formId);
    } else {
      this.setConfig(formConfig, fieldsetConfig);
    }
  }

  protected getFormId(): FormKey {
    return getFormKey(this.datasetData.type);
  }

  protected setConfig(config: FormConfig, fieldsetConfig: FormFieldsetConfig): void {
    this.allFieldConfigs = createFieldsByConfig(config);
    this.editForm = createForm(this.formId, this.allFieldConfigs);
    this.formFieldLists = createFieldLists(this.formId, this.allFieldConfigs, fieldsetConfig);

    this.setFieldListCategories(this.formFieldLists);
  }

  protected setFieldListCategories(fieldLists: FormFieldList[]): void {
    this.formCategories = [
      'all' as FormFieldsetCategory,
      ...fieldLists
        .map((item) => {
          return item.category;
        }),
    ].filter((category, index, arr) => {
      return !!category && arr.indexOf(category) === index;
    });
  }

  // eslint-disable-next-line class-methods-use-this
  protected onFormPatchedByDataset(): void {
    console.info('initial form data patched');
  }

  protected setHiddenKeys(): void {
    console.warn('no hidden form fields defined for', this.formId);
  }

  protected handleRequest(formData: DatasetFormData, isDenied?: boolean): Promise<boolean> {
    console.warn('handleRequest not implemented for', this.formId, formData, isDenied);

    return Promise.resolve(false);
  }

  protected onRequestFinished(requestSucceeded: boolean, isDenied?: boolean): void {
    if (!requestSucceeded) {
      this.processCompleted.emit('error');

      return;
    }

    this.processCompleted.emit(isDenied
      ? 'denied'
      : 'submitted');
  }

  // checking against null or undefined
  // eslint-disable-next-line class-methods-use-this
  protected isValidValue(value: FormFieldValue, key?: FormFieldKey): boolean {
    // backend validation allows no empty strings as emails
    if (key === 'email') {
      // eslint-disable-next-line no-undefined
      return value !== null && value !== undefined && value !== '';
    }

    // eslint-disable-next-line no-undefined
    return value !== null && value !== undefined;
  }

  protected patchFormByDataset(data: T): void {
    for (const key of Object.keys(this.editForm.value)) {
      const value = data[key];

      // skip for null and undefined
      if (!this.isValidValue(value)) {
        continue;
      }

      if (!Array.isArray(value)) {
        this.editForm.patchValue({
          [key]: value,
        });
      } else if (value.length) {
        // handle dynamic form fields (email, phone contact, etc.)
        // and form controls with multiple and predefined values (qualifications, etc)
        // only patch when having at least one element in array
        const fieldConfig = this.allFieldConfigs
          .find((field) => { return field.id === key; });

        if (fieldConfig.type === 'dynamic') {
          this.patchDynamicDatasetData(key as FormFieldKey, value);
        } else {
          // patching array data in FormControl works fine here, no FormArray needed
          // probably not working well with specific reactive validation rules like having at least 3 items selected
          this.editForm.patchValue({
            [key]: value,
          });
        }
      }
    }

    this.onFormPatchedByDataset();
  }

  protected getSanitizedDatasetData<U>(formData: U): U {
    const attributes = {};

    for (const key of Object.keys(formData)) {
      const value = formData[key];

      if (Array.isArray(value)) {
        attributes[key] = this.getSanitizedFormArrayData(value);

        continue;
      }

      if (FIELDS_NOT_ALLOWED_TO_BE_NULL.includes(key as FormFieldKey)) {
        continue;
      }

      if (this.isValidValue(value, key as FormFieldKey)) {
        attributes[key] = value;
      } else {
        attributes[key] = null;
      }
    }

    return attributes as U;
  }

  // patching dynamic fields
  // ! we cannot do this directly via this.editForm.patchValue !
  //  in dynamic-form-field component we clear the entire form array first and then add each form control one by one
  protected patchDynamicDatasetData(key: FormFieldKey, values: DynamicFormValues[]): void {
    if (!values?.length) {
      return;
    }

    // phone contacts, contact persons, etc.
    if (typeof values[0] === 'object') {
      this.dynamicFields[key] = values;
    } else {
      // emails, websites, etc.
      this.dynamicFields[key] = values.map((value) => {
        return {
          [key]: value,
        };
      });
    }
  }

  private getSanitizedFormArrayData(formArray: (FormFieldValue | DynamicFormValues)[]): (FormFieldValue | DynamicFormValues)[] {
    if (!formArray?.length) {
      return [];
    }

    // only checking the first item is fine, mixed types shouldn't be the case here
    if (typeof formArray[0] !== 'object') {
      return formArray
        .filter((value) => { return this.isValidValue(value as FormFieldValue); });
    }

    const formGroups: DynamicFormValues[] = [];

    for (const formGroup of formArray as DynamicFormValues[]) {
      if (!formGroup) {
        continue;
      }

      const attributes = this.getSanitizedDatasetData<DynamicFormValues>(formGroup);

      if (Object.keys(attributes).length) {
        formGroups.push(attributes);
      }
    }

    return formGroups;
  }
}
